import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography
} from '@mui/material';
import {
  Download,
  History as HistoryIcon,
  Preview,
  Refresh,
  BarChart as BarChartIcon
} from '@mui/icons-material';
import { useToast } from '../../contexts/ToastContext';
import { formatHistoryUserName } from './reportHistoryUtils';
import ReportPreviewDialog from './ReportPreviewDialog';
import ReportVisualizeDialog from './ReportVisualizeDialog';
import {
  downloadSalesReportHistoryCsv,
  downloadSalesReportHistoryPdf,
  downloadSalesReportCsv,
  downloadSalesReportPdf,
  fetchSalesReportPreviewData,
  fetchSalesReportHistory,
  resolveAxiosErrorMessage
} from '../../utils/reportCenterApi';

const TRIP_STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'PENDING_OPS', label: 'Pending Ops' },
  { value: 'CREW_ASSIGNMENT', label: 'Crew Assignment' },
  { value: 'READY_FOR_BA', label: 'Ready for BA' },
  { value: 'READY_FOR_ATC', label: 'Ready for ATC' },
  { value: 'GROUNDED', label: 'Grounded' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'CHARTER_COMPLETED', label: 'Charter Completed' },
  { value: 'CANCELLED', label: 'Cancelled' }
];

const FLIGHT_TYPE_OPTIONS = [
  { value: '', label: 'All flight types' },
  { value: 'CHARTER', label: 'Charter' },
  { value: 'NON_REVENUE', label: 'Non Revenue' },
  { value: 'TRAINING', label: 'Training' },
  { value: 'FERRY', label: 'Ferry' },
  { value: 'POSITIONING', label: 'Positioning' },
  { value: 'MAINTENANCE', label: 'Maintenance' }
];

const SECTOR_STATUS_OPTIONS = [
  { value: '', label: 'All sector statuses' },
  { value: 'PLANNED', label: 'Planned' },
  { value: 'ASSIGNED', label: 'Assigned' },
  { value: 'CREW_ACCEPTED', label: 'Crew Accepted' },
  { value: 'CREW_REJECTED', label: 'Crew Rejected' },
  { value: 'READY_FOR_BA', label: 'Ready for BA' },
  { value: 'BA_PASSED', label: 'BA Passed' },
  { value: 'BA_FAILED', label: 'BA Failed' },
  { value: 'READY_FOR_ATC', label: 'Ready for ATC' },
  { value: 'CLEARED', label: 'Cleared' },
  { value: 'ATC_PASSED', label: 'ATC Passed' },
  { value: 'ATC_FAILED', label: 'ATC Failed' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'SECTOR_COMPLETED', label: 'Sector Completed' },
  { value: 'CANCELLED', label: 'Cancelled' }
];

const PENDING_APPROVAL_OPTIONS = [
  { value: '', label: 'Any approval state' },
  { value: 'ANY', label: 'Pending any approval' },
  { value: 'AM', label: 'Pending AM approval' },
  { value: 'CAMO', label: 'Pending CAMO approval' }
];

function todayDateValue() {
  const d = new Date();
  const offset = d.getTimezoneOffset();
  const local = new Date(d.getTime() - offset * 60 * 1000);
  return local.toISOString().slice(0, 10);
}

function addDays(dateValue, days) {
  const d = new Date(`${dateValue}T00:00:00`);
  d.setDate(d.getDate() + days);
  const offset = d.getTimezoneOffset();
  const local = new Date(d.getTime() - offset * 60 * 1000);
  return local.toISOString().slice(0, 10);
}

function formatDisplayDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatDisplayDateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
}

function formatFileSize(bytes) {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

const SalesReportPanel = ({ aircraftOptions = [], aircraftLabelById = {} }) => {
  const { showToast } = useToast();
  const [subTab, setSubTab] = useState(0);
  const [selectedAircraftIds, setSelectedAircraftIds] = useState([]);
  const [dateFrom, setDateFrom] = useState(() => addDays(todayDateValue(), -90));
  const [dateTo, setDateTo] = useState(() => todayDateValue());
  const [reportDate, setReportDate] = useState(todayDateValue);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [flightType, setFlightType] = useState('');
  const [sectorStatus, setSectorStatus] = useState('');
  const [createdBy, setCreatedBy] = useState('');
  const [pendingApproval, setPendingApproval] = useState('');
  const [excludeCancelled, setExcludeCancelled] = useState(false);
  const [userOptions, setUserOptions] = useState([]);
  const [previewing, setPreviewing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadingCsv, setDownloadingCsv] = useState(false);
  const [visualizing, setVisualizing] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewReportData, setPreviewReportData] = useState(null);
  const [visualizeOpen, setVisualizeOpen] = useState(false);
  const [visualizeData, setVisualizeData] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    axios.get('/api/report-center/filter-options/users')
      .then((res) => setUserOptions(res.data.options || []))
      .catch(() => setUserOptions([]));
  }, []);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const rows = await fetchSalesReportHistory();
      setHistory(rows);
    } catch (err) {
      showToast(await resolveAxiosErrorMessage(err) || 'Failed to load history', 'error');
    } finally {
      setHistoryLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    if (subTab === 1) {
      loadHistory();
    }
  }, [subTab, loadHistory]);

  const dateRangeError = useMemo(() => {
    if (dateFrom && dateTo && dateFrom > dateTo) {
      return 'Date From must be on or before Date To';
    }
    return '';
  }, [dateFrom, dateTo]);

  const filtersPayload = useMemo(() => ({
    search: search.trim() || undefined,
    status: status || undefined,
    flightType: flightType || undefined,
    sectorStatus: sectorStatus || undefined,
    createdBy: createdBy || undefined,
    pendingApproval: pendingApproval || undefined,
    excludeCancelled: excludeCancelled || undefined
  }), [search, status, flightType, sectorStatus, createdBy, pendingApproval, excludeCancelled]);

  const requestBody = useMemo(() => ({
    aircraftIds: selectedAircraftIds,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    reportDate,
    filters: filtersPayload
  }), [selectedAircraftIds, dateFrom, dateTo, reportDate, filtersPayload]);

  const handlePreview = async () => {
    if (dateRangeError) {
      showToast(dateRangeError, 'warning');
      return;
    }
    setPreviewing(true);
    try {
      const reportData = await fetchSalesReportPreviewData(requestBody);
      setPreviewReportData(reportData);
      setPreviewOpen(true);
    } catch (err) {
      showToast(await resolveAxiosErrorMessage(err) || 'Preview failed', 'error');
    } finally {
      setPreviewing(false);
    }
  };

  const handleVisualize = async () => {
    if (dateRangeError) {
      showToast(dateRangeError, 'warning');
      return;
    }
    setVisualizing(true);
    try {
      const reportData = await fetchSalesReportPreviewData(requestBody);
      setVisualizeData(reportData);
      setVisualizeOpen(true);
    } catch (err) {
      showToast(await resolveAxiosErrorMessage(err) || 'Visualization failed', 'error');
    } finally {
      setVisualizing(false);
    }
  };

  const handleDownload = async (visualizations) => {
    if (dateRangeError) {
      showToast(dateRangeError, 'warning');
      return;
    }
    setDownloading(true);
    try {
      const suffix = dateFrom && dateTo ? `${dateFrom}_to_${dateTo}` : 'all';
      await downloadSalesReportPdf(
        { ...requestBody, visualizations },
        `Flight_Entry_Report_${suffix}.pdf`
      );
      showToast(
        visualizations?.length
          ? 'Flight Entry Report with visualizations downloaded and saved to history'
          : 'Flight Entry Report downloaded and saved to history',
        'success'
      );
      if (subTab === 1) loadHistory();
    } catch (err) {
      showToast(await resolveAxiosErrorMessage(err) || 'Download failed', 'error');
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadCsv = async () => {
    if (dateRangeError) {
      showToast(dateRangeError, 'warning');
      return;
    }
    setDownloadingCsv(true);
    try {
      const suffix = dateFrom && dateTo ? `${dateFrom}_to_${dateTo}` : 'all';
      await downloadSalesReportCsv(requestBody, `Flight_Entry_Report_${suffix}.pdf`);
      showToast('Flight Entry Report CSV downloaded', 'success');
    } catch (err) {
      showToast(await resolveAxiosErrorMessage(err) || 'CSV download failed', 'error');
    } finally {
      setDownloadingCsv(false);
    }
  };

  const handleHistoryPdf = (row) => {
    downloadSalesReportHistoryPdf(row._id, row.filename || 'Flight_Entry_Report.pdf');
  };

  const handleHistoryCsv = (row) => {
    downloadSalesReportHistoryCsv(row._id, row.filename || 'Flight_Entry_Report.pdf');
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Flight Entry Report
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Charter pipeline with summary counts, charter details, and sector legs. Trips filter by created date;
        sectors filter by departure date within the same range.
      </Typography>

      <Tabs value={subTab} onChange={(_, v) => setSubTab(v)} sx={{ mb: 2 }}>
        <Tab label="Generate" />
        <Tab label="History" icon={<HistoryIcon fontSize="small" />} iconPosition="start" />
      </Tabs>

      {subTab === 0 && (
        <Paper sx={{ p: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel id="sales-report-aircraft-label">Aircraft (optional)</InputLabel>
                <Select
                  labelId="sales-report-aircraft-label"
                  multiple
                  value={selectedAircraftIds}
                  label="Aircraft (optional)"
                  onChange={(e) => setSelectedAircraftIds(e.target.value)}
                  renderValue={(selected) => selected.map((id) => aircraftLabelById[id] || id).join(', ')}
                >
                  {aircraftOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Charter created from"
                type="date"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Charter created to"
                type="date"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Report date"
                type="date"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={reportDate}
                onChange={(e) => setReportDate(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Search"
                size="small"
                fullWidth
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Charter, title, client…"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Charter status</InputLabel>
                <Select value={status} label="Charter status" onChange={(e) => setStatus(e.target.value)}>
                  {TRIP_STATUS_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value || 'all'} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Flight type</InputLabel>
                <Select value={flightType} label="Flight type" onChange={(e) => setFlightType(e.target.value)}>
                  {FLIGHT_TYPE_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value || 'all'} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Sector status</InputLabel>
                <Select value={sectorStatus} label="Sector status" onChange={(e) => setSectorStatus(e.target.value)}>
                  {SECTOR_STATUS_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value || 'all'} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Created by</InputLabel>
                <Select value={createdBy} label="Created by" onChange={(e) => setCreatedBy(e.target.value)}>
                  <MenuItem value="">All users</MenuItem>
                  {userOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Pending approval</InputLabel>
                <Select value={pendingApproval} label="Pending approval" onChange={(e) => setPendingApproval(e.target.value)}>
                  {PENDING_APPROVAL_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value || 'all'} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={excludeCancelled}
                    onChange={(e) => setExcludeCancelled(e.target.checked)}
                  />
                }
                label="Exclude cancelled charters"
              />
            </Grid>
          </Grid>

          {dateRangeError && (
            <Alert severity="warning" sx={{ mt: 2 }}>{dateRangeError}</Alert>
          )}

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 3 }}>
            <Button
              variant="outlined"
              startIcon={previewing ? <CircularProgress size={18} /> : <Preview />}
              onClick={handlePreview}
              disabled={previewing || Boolean(dateRangeError)}
            >
              Preview
            </Button>
            <Button
              variant="outlined"
              startIcon={visualizing ? <CircularProgress size={18} /> : <BarChartIcon />}
              onClick={handleVisualize}
              disabled={visualizing || Boolean(dateRangeError)}
            >
              Visualize
            </Button>
            <Button
              variant="contained"
              startIcon={downloading ? <CircularProgress size={18} color="inherit" /> : <Download />}
              onClick={() => handleDownload()}
              disabled={downloading || Boolean(dateRangeError)}
            >
              Download PDF
            </Button>
            <Button
              variant="outlined"
              startIcon={downloadingCsv ? <CircularProgress size={18} /> : <Download />}
              onClick={handleDownloadCsv}
              disabled={downloadingCsv || Boolean(dateRangeError)}
            >
              Download CSV
            </Button>
          </Stack>
        </Paper>
      )}

      {subTab === 1 && (
        <Paper sx={{ p: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Typography variant="subtitle1">Downloaded Flight Entry Reports</Typography>
            <Button startIcon={<Refresh />} onClick={loadHistory} disabled={historyLoading}>
              Refresh
            </Button>
          </Stack>
          {historyLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : history.length === 0 ? (
            <Alert severity="info">No Flight Entry Report downloads yet. Generate one from the Generate tab.</Alert>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Downloaded</TableCell>
                  <TableCell>Generated by</TableCell>
                  <TableCell>Period</TableCell>
                  <TableCell>Aircraft</TableCell>
                  <TableCell>Charters</TableCell>
                  <TableCell>Size</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((row) => (
                  <TableRow key={row._id}>
                    <TableCell>{formatDisplayDateTime(row.downloadedAt)}</TableCell>
                    <TableCell>{formatHistoryUserName(row.downloadedBy)}</TableCell>
                    <TableCell>
                      {formatDisplayDate(row.dateFrom)} – {formatDisplayDate(row.dateTo)}
                    </TableCell>
                    <TableCell>{(row.aircraftLabels || []).join(', ') || 'All'}</TableCell>
                    <TableCell>{row.rowCount ?? '—'}</TableCell>
                    <TableCell>{formatFileSize(row.fileSize)}</TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button size="small" onClick={() => handleHistoryPdf(row)}>PDF</Button>
                        <Button size="small" onClick={() => handleHistoryCsv(row)}>CSV</Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Paper>
      )}

      <ReportPreviewDialog
        open={previewOpen}
        title="Flight Entry Report preview"
        reportData={previewReportData}
        onClose={() => {
          setPreviewOpen(false);
          setPreviewReportData(null);
        }}
      />

      <ReportVisualizeDialog
        open={visualizeOpen}
        title="Flight Entry Report"
        data={visualizeData}
        onClose={() => {
          setVisualizeOpen(false);
          setVisualizeData(null);
        }}
        onDownloadPdf={handleDownload}
      />
    </Box>
  );
};

export default SalesReportPanel;
